import {
  HeaderElement,
  HeaderMedia as HeaderMediaItem,
  MediaDisplay,
  NamesDisplay,
  NavigationStyle,
  WeddingWebsiteSiteDetails,
} from "../../../resources/modelTypes";
import { HeaderMedia } from "./media/HeaderMedia";
import { css, SerializedStyles } from "@emotion/react";
import {
  CoupleHorizontalNames,
  CoupleMastheadNames,
  CouplePlacecardNames,
  CoupleStory,
  CoupleVerticalNames,
  Countdown,
  EventDate,
  EventDateHeading,
  StickyNavbar,
  useDesign,
  withDesign,
} from "@minted/fancyclothes";
import { styleUtils } from "@minted/minted-components";
import React, { memo, ReactNode } from "react";

const styles = {
  dateHeading: css`
    margin-bottom: ${styleUtils.rem(20)};
  `,
  weddingDate: (textAlignment: string) => css`
    margin-bottom: ${styleUtils.rem(30)};
    margin-top: ${styleUtils.rem(30)};
    text-align: ${textAlignment};
  `,
  // Same content gutter as LandscapeMedia / fancyclothes Wrapper.
  wrapper: css`
    box-sizing: border-box;
    margin-left: auto;
    margin-right: auto;
    max-width: ${styleUtils.rem(1170)};
    padding-left: ${styleUtils.rem(15)};
    padding-right: ${styleUtils.rem(15)};
  `,
};

type NavItem = {
  active: boolean;
  onClick: (event: React.MouseEvent) => void;
  title: string;
  url: string;
};

type NavScope = "Inline" | "Top";

type GetDesignRule = (
  rule: string,
  scopes?: string | string[],
  design?: unknown,
  currentScopes?: string[],
) => string | null;

type NavigationProps = {
  forceScope: NavScope;
  getRule: GetDesignRule;
  navItems: NavItem[];
};

/**
 * Pins the nav to a fixed color scope (see ModularWeddingHeader below) so
 * sticky never changes its color. That pin also disables fancyclothes'
 * Navbar own fallback, which otherwise swaps a literal transparent
 * background for the page's real background once sticky — needed here since
 * the modular header doesn't guarantee the nav sits over a photo the way
 * older single-header themes did. Replicate that fallback ourselves,
 * unconditionally, mirroring Navbar's own
 * `getRule('backgroundColor', ['self'], design, [variation, ...currentScopes])`
 * resolution so the nav always reads as a real, theme-derived color.
 */
const NavigationComponent = ({
  forceScope,
  getRule,
  navItems,
}: NavigationProps) => {
  const { currentScopes } = useDesign();
  const ownBackgroundColor = getRule("backgroundColor", "Navbar", undefined, [
    forceScope,
    ...currentScopes,
  ]);
  const fallbackBackgroundColor = getRule("backgroundColor", "DesignProvider");
  const isTransparent =
    !ownBackgroundColor || ownBackgroundColor === "transparent";

  return (
    <div
      css={
        isTransparent &&
        fallbackBackgroundColor &&
        // .navbarContainer is fancyclothes' Navbar root class — targeted
        // this way because Navbar doesn't forward a className/css/ref prop.
        css`
          .navbarContainer {
            background-color: ${fallbackBackgroundColor} !important;
          }
        `
      }
    >
      <StickyNavbar forceScope={forceScope} items={navItems} />
    </div>
  );
};

const Navigation = withDesign("ModularWeddingHeaderNavigation")(
  NavigationComponent,
);

type SectionWrapperProps = {
  className: string;
  css?: SerializedStyles;
  onClick?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  topComponent?: ReactNode;
};

export type ModularWeddingHeaderProps = {
  children?: ReactNode;
  coupleNamesProps: SectionWrapperProps;
  eventDetailsWrapperProps: SectionWrapperProps;
  headerMedia: HeaderMediaItem[];
  /** A non-home page of a multi-page site. Suppresses the home-only blocks. */
  isSecondaryPage: boolean;
  navItems: NavItem[];
  ourStoryWrapperProps: SectionWrapperProps;
  siteDetails: WeddingWebsiteSiteDetails;
  textAlignment: string;
  weddingDate: string;
};

const renderNames = (
  headerDisplay: NamesDisplay | null | undefined,
  names: {
    bottomName: string;
    nameSeparator?: string;
    topName: string;
  },
) => {
  const { bottomName, nameSeparator, topName } = names;

  switch (headerDisplay) {
    case NamesDisplay.Horizontal:
      return (
        <CoupleHorizontalNames
          leftName={topName}
          nameSeparator={nameSeparator}
          rightName={bottomName}
        />
      );
    case NamesDisplay.Medium:
      return (
        <CoupleMastheadNames
          bottomName={bottomName}
          nameSeparator={nameSeparator}
          topName={topName}
        />
      );
    case NamesDisplay.Large:
      return (
        <CouplePlacecardNames
          bottomName={bottomName}
          nameSeparator={nameSeparator}
          topName={topName}
        />
      );
    case NamesDisplay.NamesOnly:
      return (
        <CoupleVerticalNames
          bottomName={bottomName}
          nameSeparator={nameSeparator}
          topName={topName}
        />
      );
    case NamesDisplay.None:
    default:
      return null;
  }
};

/**
 * Modular wedding header driven by siteDetails header axes
 * (headerDisplay, navigationStyle, mediaDisplay, headerElementOrder)
 * instead of the legacy themeIdentifier monoliths.
 */
const ModularWeddingHeader = ({
  children,
  coupleNamesProps,
  eventDetailsWrapperProps,
  headerMedia,
  isSecondaryPage,
  navItems,
  ourStoryWrapperProps,
  siteDetails,
  textAlignment,
  weddingDate,
}: ModularWeddingHeaderProps) => {
  const topName = siteDetails.yourName || "Name";
  const bottomName = siteDetails.spouseName || "Name";
  const {
    countdownText = "the big day",
    eventDateHeading = "Please join us for our wedding celebration on",
    headerDisplay,
    headerElementOrder,
    isCountdownVisible,
    isOurStoryVisible,
    isWeddingDateVisible,
    mediaDisplay: mediaDisplayValue,
    nameSeparator,
    navigationStyle = NavigationStyle.Original,
    welcomeHeading,
  } = siteDetails;

  // Default applies for undefined; ?? also covers null (destructuring defaults do not).
  const mediaDisplay = mediaDisplayValue ?? MediaDisplay.Landscape;

  // Every page renders all three elements; only the home-only blocks below differ.
  // This header only renders for sites bucketed into new-layouts, which the backend
  // seeds headerElementOrder for at creation/bucketing time, so it's always populated.
  const orderedElements = headerElementOrder!;

  const { topComponent: coupleNamesTopComponent, ...coupleNamesDivProps } =
    coupleNamesProps;
  const { topComponent: eventDetailsTopComponent, ...eventDetailsDivProps } =
    eventDetailsWrapperProps;
  const { topComponent: ourStoryTopComponent, ...ourStoryDivProps } =
    ourStoryWrapperProps;

  const introHeading = isOurStoryVisible ? welcomeHeading || "Our Story" : null;

  const showWeddingDate =
    !isSecondaryPage &&
    isWeddingDateVisible &&
    Boolean(siteDetails.weddingDate);

  return (
    <div>
      {orderedElements.map((elementId) => {
        switch (elementId) {
          case HeaderElement.Names: {
            // Null for NamesDisplay.None, which drops the element entirely.
            const namesContent = renderNames(headerDisplay, {
              bottomName,
              nameSeparator,
              topName,
            });

            if (!namesContent) {
              return null;
            }

            return (
              <div key={elementId} {...coupleNamesDivProps}>
                {coupleNamesTopComponent}
                {namesContent}
              </div>
            );
          }
          case HeaderElement.Navigation:
            return (
              <Navigation
                forceScope={
                  navigationStyle === NavigationStyle.Alternate
                    ? "Top"
                    : "Inline"
                }
                key={elementId}
                navItems={navItems}
              />
            );
          case HeaderElement.Media:
            return (
              <HeaderMedia
                alt={`${topName} and ${bottomName}`}
                headerMedia={headerMedia}
                key={elementId}
                mediaDisplay={mediaDisplay}
              />
            );
          default:
            return null;
        }
      })}

      {showWeddingDate && (
        <div css={styles.wrapper}>
          <div
            {...eventDetailsDivProps}
            css={[styles.weddingDate(textAlignment), eventDetailsDivProps.css]}
          >
            {eventDetailsTopComponent}
            <div css={styles.dateHeading}>
              <EventDateHeading>{eventDateHeading}</EventDateHeading>
            </div>
            <EventDate date={weddingDate} format="MMMM d, yyyy" />
            <Countdown
              countdownText={countdownText}
              date={weddingDate}
              isCountdownVisible={isCountdownVisible}
            />
          </div>
        </div>
      )}

      {!isSecondaryPage && isOurStoryVisible && introHeading && (
        <CoupleStory
          contentTextAlignment={textAlignment}
          introHeading={introHeading}
          topComponent={ourStoryTopComponent}
          {...ourStoryDivProps}
        >
          {children}
        </CoupleStory>
      )}
    </div>
  );
};

const MemoizedModularWeddingHeader = memo(ModularWeddingHeader);

export { MemoizedModularWeddingHeader as ModularWeddingHeader };
